import { ref, computed, onMounted, onBeforeMount } from 'vue'
import config from '../utils/config'

export function noticeMixin({ props, root, emit }) {
  const newContainer = ref(props.container || config.defaultContainerElement)
  const newDuration = ref(props.duration || config.defaultNotificationDuration)
  const isActive = ref(false)
  const parentTop = ref(null)
  const parentBottom = ref(null)
  const timer = ref(null)
  const maxNotifications = 5

  /**
   * @returns {HTMLElement}
   * @description Returns the element container that we need for the
   * toast message the is being created i.e top or bottom.
   */
  const correctParent = computed(() => {
    switch (props.position) {
      case 'top-right':
      case 'top':
      case 'top-left':
        return parentTop.value

      default:
        return parentBottom.value
    }
  })

  /**
   * @returns {Object}
   * @description Returns the transition configuration for the toast
   * message that is being created.
   */
  const transition = computed(() => {
    switch (props.position) {
      case 'top-right':
      case 'top':
      case 'top-left':
        return 'fade-down'
      default:
        return 'fade-up'
    }
  })

  /**
   * @returns {Boolean}
   * @description Returns a boolean flag to determine if the toast message
   * should be queued up or not.
   */
  const shouldQueue = () => {
    const queue =
      props.queue !== undefined ? props.queue : config.defaultNoticeQueue

    if (!queue) return false

    return (
      parentTop.value.childElementCount >= maxNotifications ||
      parentBottom.value.childElementCount >= maxNotifications
    )
  }

  /**
   * @description This closes the notification message
   */
  const close = () => {
    clearTimeout(timer.value)
    isActive.value = false
    emit('close')

    // Timeout for the animation complete before destroying
    setTimeout(() => {
      props.destroy()
    }, 0)
  }

  /**
   * @description displays a toast notification, this will br recursively
   * called if the message needs to be queued, else we will just add the
   * toast notification into the requested container, if the toast is set
   * to "indefinite" then we will not add the close timeout, and the user
   * would have to close it manually.
   */
  const showNotice = () => {
    if (shouldQueue()) {
      // Call recursively if should queue
      setTimeout(() => showNotice(), 250)
      return
    }

    correctParent.value.insertAdjacentElement('afterbegin', root.value)

    isActive.value = true

    if (!props.indefinite) {
      timer.value = setTimeout(() => close(), newDuration.value)
    }
  }

  /**
   * @description Sets up the required containers for the toast elements to
   * be placed in, these will only be created once, and then each time a new
   * toast is created they will be placed in the existing element.
   */
  const setupContainer = () => {
    parentTop.value = document.querySelector(
      (newContainer.value ? newContainer.value : 'body') +
        '>.ak-notice-container.ak-notice-container--top'
    )
    parentBottom.value = document.querySelector(
      (newContainer.value ? newContainer.value : 'body') +
        '>.ak-notice-container.ak-notice-container--bottom'
    )

    if (parentTop.value && parentBottom.value) return

    /* istanbul ignore else */
    if (!parentTop.value) {
      parentTop.value = document.createElement('div')
      parentTop.value.className = 'ak-notice-container ak-notice-container--top'
    }

    /* istanbul ignore else */
    if (!parentBottom.value) {
      parentBottom.value = document.createElement('div')
      parentBottom.value.className =
        'ak-notice-container ak-notice-container--bottom'
    }

    const container =
      document.querySelector(newContainer.value) || document.body

    container.appendChild(parentTop.value)
    container.appendChild(parentBottom.value)

    if (newContainer.value) {
      parentTop.value.classList.add('has-custom-container')
      parentBottom.value.classList.add('has-custom-container')
    }
  }

  onBeforeMount(() => {
    setupContainer()
  })

  onMounted(() => {
    showNotice()
  })

  return {
    isActive,
    parentTop,
    parentBottom,
    newContainer,
    correctParent,
    transition,
    newDuration,
    shouldQueue,
    close,
    showNotice,
    setupContainer,
  }
}

export const notificationNoticeProps = {
  /**
   * @default false
   * @type {Boolean}
   * @description Determines if the notification should sty open
   * until the user closes it manually.
   */
  indefinite: {
    type: Boolean,
    default: false,
  },
}

export const noticeProps = {
  /**
   * @type {String}
   * @description Type (color) of the toast
   */
  type: {
    type: String,
    default: 'grey-darker',
  },

  /**
   * @type {String}
   * @description	Message text (can contain HTML).
   */
  message: {
    type: String,
    default: undefined,
  },

  /**
   * @type {String}
   * @description Visibility duration in milliseconds
   */
  duration: {
    type: Number,
    default: 0,
  },

  /**
   * @type {String}
   * @description If should queue with others notices
   * (snackbar/toast/notification)
   */
  queue: {
    type: Boolean,
    default: undefined,
  },

  /**
   * @type {String}
   * @description Which position the toast will appear
   */
  position: {
    type: String,
    default: 'top',
    /* istanbul ignore next */

    validator(value) {
      return (
        [
          'top-left',
          'top',
          'top-right',
          'bottom-right',
          'bottom',
          'bottom-left',
        ].indexOf(value) > -1
      )
    },
  },

  /**
   * @type {String}
   * @description 	DOM element the toast will be created on.
   *
   * Note that this also changes the position of the toast from fixed to absolute.
   * Meaning that the container should be fixed. Also note that this will override
   * the defaultContainerElement if you specified it in your AkeroCore Constructor
   * Options. See Constructor options for more details.
   */
  container: {
    type: String,
    default: undefined,
  },

  destroy: {
    type: Function,
    default: undefined,
  },
}

export const snackbarProps = {
  actionText: {
    type: String,
    default: 'OK',
  },
  actionDismiss: {
    type: Boolean,
    default: false,
  },
  onAction: {
    type: Function,
    default: /* istanbul ignore next */ () => {},
  },
  indefinite: {
    type: Boolean,
    default: false,
  },
  inverse: {
    type: Boolean,
    default: false,
  },
}
